// extends
import { ExtendedContainer, ExtendedGraphics, SkySpine, delay, getRandomInt, soundService } from "@aceroll/core";

class Ra extends ExtendedContainer {
    _state = 'main';
    _orientation = 'landscape';

    animations = {
        idle: 'idle_main',
        idle_move: 'idle_light_move',
        idle_look: 'idle_look_right_hand',
        idle_hit: 'idle_staff_hit',
        win_charging: 'win_charging_lighting',
        win_bolt: 'win_lighting_bolt',
        win_long_jump: 'win_long_jump',
        win_no_jump: 'win_no_jump_lighting_eff',
        win_short_jump: 'win_short_jump'
    };

    sounds = {
        main: {
            win_charging: {
                sound: 'character_flash_2',
                delay: 1900
            },
            win_bolt: 'character_flash_explotion',
            win_long_jump: {
                sound: 'character_flash_3',
                delay: 750
            },
            win_no_jump: {
                sound: 'character_flash_explotion',
                delay: 750
            },
            win_short_jump: {
                sound: 'character_flash_2',
                delay: 1150
            }
        },
        bonus: {
            win_charging: {
                sound: 'character_bonus_flash_1',
                delay: 1300
            },
            win_bolt: {
                sound: 'character_bonus_flash_1',
                delay: 250
            },
            win_long_jump: {
                sound: 'character_bonus_flash_3',
                delay: 550
            },
            win_no_jump: {
                sound: 'character_bonus_flash_2',
                delay: 500
            },
            win_short_jump: {
                sound: 'character_bonus_flash_2',
                delay: 550
            }
        }
    };

    scenarios = {
        initialIdle: ['idle_move', 'idle_look', 'idle_hit'],
        idle: [
            ['idle', 'idle', 'idle'],
            ['idle', 'idle_move', 'idle'],
            ['idle', 'idle_look', 'idle']
        ],
        minWin: ['win_charging', 'win_no_jump'],
        averageWin: ['win_bolt', 'win_no_jump'],
        bigWin: 'win_short_jump',
        hugeWin: 'win_long_jump'
    };

    prevIdle = null;

    nextTask = null;

    spineLandscape = null;
    spinePortrait = null;
    portraitMask = null;

    _spines = {
        main: {
            landscape: null,
            portrait: null
        },
        bonus: {
            landscape: null,
            portrait: null
        }
    };

    constructor() {
        super('character');

        this._spines = {
            main: {
                landscape: new SkySpine('spine-character_main_landscape'),
                portrait: new SkySpine('spine-character_main_portrait')
            },
            bonus: {
                landscape: new SkySpine('spine-character_bonus_landscape'),
                portrait: new SkySpine('spine-character_bonus_portrait')
            }
        };

        this._adjustPosition();

        this.createPortraitMask();

        this.addChild(
            this._spines.main.landscape,
            this._spines.main.portrait,
            this._spines.bonus.landscape,
            this._spines.bonus.portrait,
            this.portraitMask
        );

        // debug only
        window.playCharacter = this.play.bind(this);
        window.characterToBonus = () => {
            this.state = 'bonus';
        };
    }

    set state(value) {
        this._state = value;

        if (value === 'bonus') {
            this.switchToBonus();
        } else {
            this.switchToMain();
        }
    }

    get state() {
        return this._state;
    }

    set orientation(value) {
        this._orientation = value;

        if (value === 'landscape') {
            this.onLandscape();
        } else {
            this.onPortrait();
        }
    }

    get orientation() {
        return this._orientation;
    }

    get spines() {
        return [
            this._spines.main.landscape,
            this._spines.main.portrait,
            this._spines.bonus.landscape,
            this._spines.bonus.portrait
        ];
    }

    _adjustPosition() {
        this._spines.bonus.landscape.position.set(30, -80);
        this._spines.bonus.portrait.position.set(0, -80);
    }

    async playInitialIdle() {
        const index = getRandomInt(0, this.scenarios.initialIdle.length - 1);

        this.nextTask = this.playIdleSet;

        const scenario = this.scenarios.initialIdle[index];

        this.currentPromise = await Promise.all(
            this.spines.map(spine => spine.setImmediately(this.animations[scenario]))
        );

        if (this.nextTask) {
            this.nextTask();
        }
    }

    async playIdleSet() {
        const index = getRandomInt(0, this.scenarios.idle[0].length - 1, this.prevIdle);
        this.prevIdle = index;

        this.resetAll();

        this.nextTask = this.playIdleSet;

        const scenario = this.scenarios.idle[index];

        this.currentPromise = await Promise.all(this.spines.map(spine => spine.setImmediately(scenario.map(animation => this.animations[animation]))));

        if (this.nextTask) {
            this.nextTask();
        }
    }

    async playWin(winType) {
        if (winType === 'minWin') {
            const random = getRandomInt(0, 1);

            if (!random) {
                return;
            }
        }

        this.nextTask = async () => {
            this.nextTask = this.playIdleSet;

            switch (winType) {
                case 'minWin':
                    await this.playMinWin();
                    break;
                case 'averageWin':
                    await this.playAverageWin();
                    break;
                case 'bigWin':
                    await this.playBigWin();
                    break;
                case 'hugeWin':
                    await this.playHugeWin();
                    break;
            }

            if (this.nextTask) {
                this.nextTask();
            }
        }

        this.resetAll();
    }

    async playMinWin() {
        const random = getRandomInt(0, this.scenarios.minWin.length - 1);

        const scenario = this.scenarios.minWin[random];

        const sound = this.sounds[this.state][scenario];

        if (sound) {
            soundService.play(sound);
        }

        await Promise.all(this.spines.map(s => s.setImmediately(this.animations[scenario])));
    }

    async playAverageWin() {
        const random = getRandomInt(0, this.scenarios.averageWin.length - 1);

        const scenario = this.scenarios.averageWin[random];

        const sound = this.sounds[this.state][scenario];

        if (sound) {
            soundService.play(sound);
        }

        await Promise.all(this.spines.map(s => s.setImmediately(this.animations[scenario])));
    }

    async playBigWin() {
        const scenario = this.scenarios.bigWin;

        const sound = this.sounds[this.state][scenario];

        if (sound) {
            soundService.play(sound);
        }

        await Promise.all(this.spines.map(s => s.setImmediately(this.animations[scenario])));
    }

    async playHugeWin() {
        const scenario = this.scenarios.hugeWin;

        const sound = this.sounds[this.state][scenario];

        if (sound) {
            soundService.play(sound);
        }

        await Promise.all(this.spines.map(s => s.setImmediately(this.animations[scenario])));
    }

    resetAll() {
        this.spines.forEach(s => s.reset());
    }

    turnOffAll() {
        this.spines.forEach(s => s.visible = false);
    }

    onLandscape() {
        // this.removeMask();

        this.turnOffAll();

        this._spines[this.state].landscape.visible = true;
    }

    onPortrait() {
        // this.applyMask();

        this.turnOffAll();

        this._spines[this.state].portrait.visible = true;
    }

    switchToBonus() {
        this.turnOffAll();

        this._spines.bonus[this.orientation].visible = true;
    }

    switchToMain() {
        this.turnOffAll();

        this._spines.main[this.orientation].visible = true;
    }


    applyMask() {
        if (this.portraitMask) {
            this.mask = this.portraitMask;
            this.portraitMask.visible = true;
        }
    }

    removeMask() {
        if (this.mask !== null) {
            this.mask = null;
        }
        this.portraitMask.visible = false;
    }

    createPortraitMask() {
        const { width, height } = this;

        this.portraitMask = new ExtendedGraphics('character_mask');
        this.portraitMask.beginFill(0x000000);
        this.portraitMask.drawRect(-width / 2, -height / 2, 1080, 600);
        this.portraitMask.endFill();
        this.portraitMask.visible = false;
    }

    onOrientationChange(orientation) {
        switch (orientation) {
            case 'landscape':
                this.spinePortrait.visible = false;
                this.spineLandscape.visible = true;
                break;
            case 'portrait':
                this.spineLandscape.visible = false;
                this.spinePortrait.visible = true;
                break;
        }
    }

    // debug method
    async play(animation, sound, soundDelay = 0) {
        soundService.play({
            sound: sound,
            delay: soundDelay || null
        });

        await Promise.all(this.spines.map(s => s.setImmediately(animation)));
    }
}

export default Ra;